'use client';

import { PLACEHOLDER_IMAGE_SRC } from '@/lib/constants/assets';

import React, { useMemo } from 'react';
import { Package } from 'lucide-react';
import { useCurrentOrders } from '@/hooks/useOrders';
import { useOrderProductImages } from '@/hooks/useOrderProductImages';
import { EmptyState } from './EmptyState';
import { Badge } from './ui/Badge';
import { formatPrice, formatDate } from '@/lib/utils/format';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useBasePath } from '@/providers/BasePathProvider';

export function AccountOrdersTab() {
    const { orders, isLoading, error } = useCurrentOrders();
    const router = useRouter();
    const { buildPath } = useBasePath();
    const allItems = useMemo(() => orders.flatMap(o => o.items ?? []), [orders]);
    const productImages = useOrderProductImages(allItems);

    if (isLoading) {
        return (
            <div className="py-6 px-3 pb-24">
                <div className="space-y-4">
                    {Array.from({ length: 3 }).map((_, index) => (
                        <div
                            key={index}
                            className="h-40 animate-pulse rounded-xl bg-slate-50"
                        />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="py-6 px-3 pb-24">
                <div className="rounded-lg border border-red-100 bg-red-50 p-6 text-sm text-red-700">
                    {error.message}
                </div>
            </div>
        );
    }

    if (orders.length === 0) {
        return (
            <div className="py-6 px-3 pb-24">
                <EmptyState
                    icon={Package}
                    title="No orders yet"
                    description="When you place orders, they'll appear here for easy tracking."
                />
            </div>
        );
    }

    return (
        <div className="pb-24">
            {/* Header */}
            <div className="mb-6">
                <h2 className="text-3xl font-bold text-hsecondary">Order History</h2>
                <p className="text-sm text-hmuted mt-1">View and track your past purchases.</p>
            </div>

            <div className="space-y-4">
                {orders.map((order) => {
                    const itemCount = (order.items?.length || 0) + ((order.customProducts as any[])?.length || 0);

                    const badgeVariant =
                        order.orderStatus === 'Delivered' ? 'success' :
                            (order.orderStatus === 'Out for Delivery' || order.orderStatus === 'Picking & Packing' || order.orderStatus === 'In Fulfillment') ? 'primary' :
                                order.orderStatus === 'Pending' ? 'warning' :
                                    order.orderStatus === 'Processing' ? 'primary' : 'gray';

                    return (
                        <div
                            key={order._id}
                            className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6"
                        >
                            {/* Top row: order info + price */}
                            <div className="flex items-start justify-between gap-4 mb-4">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap mb-1">
                                        <h3 className="text-base font-semibold text-hsecondary shrink-0">
                                            Order #{order._id?.slice(-8).toUpperCase()}
                                        </h3>
                                        <Badge variant={badgeVariant} size="sm">
                                            {order.orderStatus}
                                        </Badge>
                                    </div>
                                    <p className="text-xs text-hmuted">
                                        Placed on {formatDate(order.createdAt || new Date(), 'long')} &bull; {itemCount} item{itemCount !== 1 ? 's' : ''}
                                    </p>
                                </div>
                                <p className="text-2xl font-bold text-hsecondary shrink-0">
                                    {formatPrice(order.grandTotal || 0)}
                                </p>
                            </div>

                            {/* Product thumbnails + names */}
                            <div className="space-y-2 mb-4">
                                {(() => {
                                    const customProducts = (order.customProducts || []) as any[];
                                    const hasItems = (order.items && order.items.length > 0) || customProducts.length > 0;
                                    if (!hasItems) return <p className="text-sm text-hmuted">No items found</p>;
                                    return (
                                        <>
                                            {order.items && order.items.map((item, index) => {
                                                const variantImg = item?.productVariantData?.media?.[0]?.file;
                                                const isPlaceholder = !variantImg || variantImg === '/placeholder.png';
                                                const productId = (item.productVariantData as any).productId;
                                                const itemImage = isPlaceholder
                                                    ? (productImages[productId] || PLACEHOLDER_IMAGE_SRC)
                                                    : variantImg;
                                                return (
                                                <div key={item.productVariantId || index} className="flex items-center gap-3 min-w-0">
                                                    <div className="relative w-10 h-10 sm:w-18 sm:h-18 rounded-lg bg-slate-100 shrink-0 overflow-hidden">
                                                        <Image
                                                            src={itemImage}
                                                            alt={item?.productVariantData?.name || 'Product image'}
                                                            fill
                                                            className="object-cover"
                                                            sizes="40px"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                        <p className="text-sm font-medium text-hsecondary truncate">
                                                            {(item.productVariantData as any)?.productName || item.productVariantData?.name || 'Unknown Product'}
                                                        </p>
                                                        {(item.productVariantData as any)?.productName && item.productVariantData?.name && (
                                                            <p className="text-xs text-hmuted truncate">{item.productVariantData.name}</p>
                                                        )}
                                                    </div>
                                                </div>
                                                );
                                            })}
                                            {customProducts.map((cp: any, index: number) => {
                                                const name = typeof cp === 'string' ? cp : (cp.name || 'Custom Item');
                                                return (
                                                <div key={`custom-${index}`} className="flex items-center gap-3 min-w-0">
                                                    <div className="relative w-10 h-10 sm:w-18 sm:h-18 rounded-lg bg-slate-100 shrink-0 overflow-hidden">
                                                        <Image
                                                            src={PLACEHOLDER_IMAGE_SRC}
                                                            alt={name}
                                                            fill
                                                            className="object-cover"
                                                            sizes="40px"
                                                        />
                                                    </div>
                                                    <div className="flex flex-col min-w-0">
                                                        <p className="text-sm font-medium text-hsecondary truncate">{name}</p>
                                                    </div>
                                                </div>
                                                );
                                            })}
                                        </>
                                    );
                                })()}
                            </div>

                            {/* Bottom: View Details */}
                            <div className="pt-4 border-t border-slate-100 flex justify-end">
                                <button
                                    onClick={() => router.push(buildPath(`/account/orders/${order._id}`))}
                                    className="border border-slate-300 rounded-full px-5 py-2 text-sm text-hsecondary hover:bg-slate-50 transition-colors"
                                >
                                    View Details
                                </button>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
